// ═══════════════════════════════════════════════════════════════════════
//  Domain Types — designed to map cleanly to API model / DB schema
// ═══════════════════════════════════════════════════════════════════════

/** Status shared across payments and artifacts */
export type DeliveryStatus = "pending" | "in_progress" | "completed" | "overdue" | "upcoming"

/** Priority level for deliverables */
export type Priority = "low" | "medium" | "high"

/** A person or organization involved in a delivery */
export interface Party {
  id: string
  name: string
  initials: string
  avatarUrl?: string
}

/** Artifact type taxonomy */
export type ArtifactCategory = "document" | "audio" | "video" | "image"

/** Base fields shared by every delivery item */
export interface DeliveryItemBase {
  id: string
  title: string
  description: string
  /** ISO date string (YYYY-MM-DD) */
  dueDate: string
  status: DeliveryStatus
  dealId: string
  dealName: string
  from: Party
  to: Party
  /** "send" = you owe this, "receive" = owed to you */
  direction: "send" | "receive"
}

// ── Automation / trigger types ──────────────────────────────────────

/** Trigger when a specific deliverable is completed */
export interface OnDeliveryTrigger {
  type: "on_delivery"
  /** ID of the artifact/deliverable that gates this payment */
  deliverableId: string
  /** Human-readable label for the triggering deliverable */
  deliverableTitle: string
}

/** Trigger on a specific calendar date */
export interface OnDateTrigger {
  type: "on_date"
  /** ISO date when the automation fires */
  date: string
}

/** Recurring payment on a schedule */
export interface RecurringTrigger {
  type: "recurring"
  frequency: "weekly" | "monthly" | "quarterly" | "yearly"
  /** Optional: next occurrence ISO date */
  nextDate?: string
}

/** Streaming / continuous revenue (e.g. royalties per play) */
export interface StreamingTrigger {
  type: "streaming"
  /** Revenue source label */
  source: string
  /** Optional: per-unit description (e.g. "per stream") */
  unitLabel?: string
}

/** Payment gated behind explicit approval from a designated party */
export interface RequiresApprovalTrigger {
  type: "requires_approval"
  /** Who must approve before the payment is released */
  approverId: string
  approverName: string
}

/** Discriminated union of all payment automation triggers */
export type PaymentAutomation = OnDeliveryTrigger | OnDateTrigger | RecurringTrigger | StreamingTrigger | RequiresApprovalTrigger

/** A monetary payment between parties */
export interface Payment extends DeliveryItemBase {
  kind: "payment"
  /** Amount in cents to avoid floating-point issues */
  amountCents: number
  currency: string
  /** Optional automation trigger attached to this payment */
  automation?: PaymentAutomation
}

/** A non-monetary deliverable (file, recording, document, etc.) */
export interface Artifact extends DeliveryItemBase {
  kind: "artifact"
  category: ArtifactCategory
  /** Human-readable type label */
  fileType: string
}

/** Discriminated union of all delivery item types */
export type DeliveryItem = Payment | Artifact

// ── Detail types (used by DeliverySheet) ────────────────────────────

export interface ActivityLogEntry {
  id: string
  timestamp: string
  type: "created" | "status_changed" | "uploaded" | "approved" | "rejected"
       | "payment_submitted" | "payment_processed" | "comment_added"
       | "automation_triggered" | "reminder_sent"
  actor: Party
  description: string
}

export interface DeliveryComment {
  id: string
  author: Party
  createdAt: string
  text: string
}

export interface RelatedDelivery {
  id: string
  title: string
  kind: "payment" | "artifact"
  relationship: "blocks" | "triggered_by" | "same_deal"
}

export interface PaymentMethod {
  type: "wire" | "ach" | "check" | "platform_escrow"
  accountLast4?: string
  bankName?: string
}

export interface PaymentTransaction {
  referenceId: string
  processedAt?: string
  memo?: string
  feesCents?: number
  netAmountCents?: number
}

export interface StreamingBreakdown {
  platform: string
  plays: number
  amountCents: number
  period: string
}

export interface RecurringHistoryEntry {
  id: string
  date: string
  amountCents: number
  status: "completed" | "pending" | "failed"
}

export interface FileMetadata {
  fileName: string
  fileSizeBytes: number
  mimeType: string
  uploadedAt?: string
  uploadedBy?: Party
}

export interface ArtifactVersion {
  id: string
  versionNumber: number
  uploadedAt: string
  uploadedBy: Party
  changeNotes?: string
  fileSizeBytes: number
}

export interface ArtifactApproval {
  status: "pending_review" | "approved" | "rejected" | "revision_requested"
  reviewedBy?: Party
  reviewedAt?: string
  comments?: string
}

export interface DeliveryItemDetailFields {
  activityLog: ActivityLogEntry[]
  comments: DeliveryComment[]
  relatedItems: RelatedDelivery[]
  internalNotes?: string
  method?: PaymentMethod
  transaction?: PaymentTransaction
  streamingBreakdown?: StreamingBreakdown[]
  recurringHistory?: RecurringHistoryEntry[]
  file?: FileMetadata
  versions?: ArtifactVersion[]
  approval?: ArtifactApproval
}

/** Row in the deliverables table view */
export interface Deliverable {
  id: string
  title: string
  dealName: string
  dueDate: string
  status: DeliveryStatus
  assignee: string
  priority: Priority
}

// ── Chart visualisation types ────────────────────────────────────────

export interface ChartPoint {
  monthIdx: number
  actual: number | null
  projected: number | null
  itemId?: string
  label: string
  txn?: { title: string; amountCents: number; direction: "send" | "receive"; dueDate: string }
}

export interface MonthTick {
  idx: number
  label: string
}

// ── Type guards ─────────────────────────────────────────────────────

export function isPayment(item: DeliveryItem): item is Payment {
  return item.kind === "payment"
}

export function isArtifact(item: DeliveryItem): item is Artifact {
  return item.kind === "artifact"
}

export interface ConnectedAccount {
  id: string
  label: string
  institution: string
  lastFour: string
  balanceCents: number
  type: "checking" | "savings" | "card"
}
